/* ==========================================================================
   cdc-load-more.js — Pagination « Charger plus » des pages collection
   --------------------------------------------------------------------------
   ROLE
   Remplace la pagination numerotee par un bouton qui ajoute le lot suivant a la
   suite de la grille. Chaque clic charge un lot, et un seul.

   AUCUN CHARGEMENT AUTOMATIQUE — demande marketing du 17/09/2026 : le visiteur
   reclique sur « Charger plus » pour chaque lot. Une premiere version enchainait
   3 lots au defilement apres le clic ; elle a ete retiree. Ne pas la reintroduire
   sans nouvelle validation.

   CHARGEMENT
   layout/theme.liquid, sur les pages collection uniquement, en `defer` — le DOM
   est donc complet a l'execution.

   MARKUP ATTENDU : snippets/c_load-more.liquid

   POURQUOI ?sections= ET PAS ?section_id=
   Le bundle Sharp utilise `?section_id=` pour ses filtres. Cette forme rend
   TOUJOURS depuis le theme publie, meme sous `preview_theme_id` : la
   fonctionnalite serait intestable en preprod. `?sections=` (forme JSON) honore
   l'apercu. `data-section-id` porte deja l'identifiant prefixe
   (template--XXX__collection) attendu par cette API.

   POURQUOI ON NE PREND QUE LES [role="listitem"]
   La grille (sections/collection.liquid) insere les bannieres merch par
   marqueurs texte [SPLIT-N] a des positions RELATIVES a la page courante. Sans
   filtre, le lot 2 replacerait ses bannieres aux memes positions visuelles
   qu'au lot 1. Les merch-breaks sont rendus dans des <div> sans role, alors que
   chaque produit porte role="listitem" : ne prendre que ces derniers les ecarte.

   BFCACHE
   Aucun ecouteur `unload` n'est pose ici : il disqualifierait la page du
   bfcache, qui est le vrai mecanisme de reprise au retour depuis une fiche
   produit. Le repli sessionStorage ne sert qu'aux retours a froid.
   ========================================================================== */
(function () {
  'use strict';

  if (window.customElements && customElements.get('cdc-load-more')) return;

  var GRID_SELECTOR = '[data-keep-class-key="collection-grid"]';
  var ANCHOR_KEY = 'cdc-lm-anchor';

  /* Substitue %1 et %2 dans un gabarit de locale. */
  function fill(template, a, b) {
    if (!template) return '';
    return String(template).replace('%1', a).replace('%2', b);
  }

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) ? fallback : n;
  }

  /* Cle de memorisation : la collection, filtres et tri compris, mais sans le
     numero de page — sinon un retour sur ?page=3 ne retrouverait pas l'ancre
     posee alors qu'on etait sur ?page=2. */
  function anchorKey() {
    var url = new URL(window.location.href);
    url.searchParams.delete('page');
    return url.pathname + url.search;
  }

  class CdcLoadMore extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-lm-ready')) return;
      this.setAttribute('data-lm-ready', '');

      var host = this.closest('[collection-grid]') || document;
      this.grid = host.querySelector(GRID_SELECTOR);
      if (!this.grid) return;

      this.button = this.querySelector('[data-lm-button]');
      this.status = this.querySelector('[data-lm-status]');
      this.bar = this.querySelector('[data-lm-bar]');
      this.i18n = this.readI18n();

      this.busy = false;
      this.anchors = [];

      this.onButtonClick = this.onButtonClick.bind(this);
      if (this.button) this.button.addEventListener('click', this.onButtonClick);

      this.setupScrollWatch();
      this.setupAnchorCapture();
      this.restoreAnchor();
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener('click', this.onButtonClick);
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
    }

    readI18n() {
      var node = this.querySelector('[data-lm-i18n]');
      if (!node) return {};
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return {};
      }
    }

    /* ---------------------------------------------------------------- clic */

    onButtonClick(event) {
      /* Clic milieu, ctrl-clic, nouvel onglet : on laisse le lien faire son
         travail. C'est le repli qui garantit l'acces aux pages suivantes. */
      if (event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      event.preventDefault();
      this.load();
    }

    /* ------------------------------------------------------------ chargement */

    load() {
      var self = this;
      var next = this.dataset.nextUrl;
      if (!next || this.busy) return Promise.resolve();

      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.classList.add('is-loading');
      this.classList.remove('has-error');
      if (this.button) this.button.setAttribute('aria-disabled', 'true');
      this.setStatus(this.i18n.loading);

      var url = new URL(next, window.location.origin);
      url.searchParams.set('sections', this.dataset.sectionId);

      /* Pas d'en-tete Accept: application/json — sur une URL /collections/*, il
         fait servir a Shopify la representation JSON de la collection et
         court-circuite la Section Rendering API. */
      return fetch(url.toString())
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (payload) {
          var html = payload[self.dataset.sectionId];
          if (!html) throw new Error('Section absente de la reponse');
          var doc = new DOMParser().parseFromString(html, 'text/html');
          self.appendBatch(doc);
          self.adoptState(doc);
        })
        .catch(function (error) {
          self.setStatus(self.i18n.error);
          self.classList.add('has-error');
          if (window.console) console.warn('[cdc-load-more]', error);
        })
        .then(function () {
          self.busy = false;
          self.setAttribute('aria-busy', 'false');
          self.classList.remove('is-loading');
          if (self.button) self.button.removeAttribute('aria-disabled');
        });
    }

    appendBatch(doc) {
      var sourceGrid = doc.querySelector(GRID_SELECTOR);
      if (!sourceGrid) return;
      var items = sourceGrid.querySelectorAll(':scope > [role="listitem"]');
      if (!items.length) return;

      var from = toInt(this.dataset.shown, 0) + 1;
      var page = toInt(this.dataset.currentPage, 1) + 1;

      /* Le lot 1 n'a pas d'ancre : on la pose au premier ajout, sinon le suivi
         d'URL ne saurait pas revenir a la page 1 quand on remonte. */
      this.ensureFirstAnchor();

      var fragment = document.createDocumentFragment();
      var anchor = this.buildAnchor(page, from, from + items.length - 1);
      fragment.appendChild(anchor);
      for (var i = 0; i < items.length; i++) fragment.appendChild(items[i]);
      this.grid.appendChild(fragment);

      this.registerAnchor(anchor);

      /* Chaque lot est demande par un clic : le focus suit sur son repere, pour
         que le clavier et le lecteur d'ecran reprennent au premier produit ajoute
         plutot que sur le bouton, desormais en bas de la grille. */
      var heading = anchor.querySelector('[tabindex]');
      if (heading) heading.focus({ preventScroll: true });
    }

    buildAnchor(page, from, to) {
      /* role="listitem" : un enfant direct d'un role="list" doit en etre un,
         sinon plusieurs lecteurs d'ecran ignorent le contenu. `sr-only` le sort
         du flux (position:absolute) : aucune ligne de grille consommee. */
      var wrapper = document.createElement('div');
      wrapper.setAttribute('role', 'listitem');
      wrapper.className = 'sr-only';
      wrapper.setAttribute('data-lm-batch', '');
      wrapper.dataset.lmPage = String(page);

      var heading = document.createElement('h2');
      heading.setAttribute('tabindex', '-1');
      heading.textContent = fill(this.i18n.batch, from, to);
      wrapper.appendChild(heading);
      return wrapper;
    }

    ensureFirstAnchor() {
      if (this.firstAnchorDone) return;
      this.firstAnchorDone = true;
      var page = toInt(this.dataset.currentPage, 1);
      var size = toInt(this.dataset.pageSize, 0);
      var shown = toInt(this.dataset.shown, 0);
      var anchor = this.buildAnchor(page, shown - size + 1, shown);
      this.grid.insertBefore(anchor, this.grid.firstChild);
      /* En tete de liste : l'ancre du lot 1 doit rester la premiere du tableau. */
      this.anchors.unshift(anchor);
    }

    /* Reprend l'etat calcule par le serveur pour le lot qui vient d'arriver :
       plus fiable que de le recalculer ici, et cela suit automatiquement les
       filtres actifs. */
    adoptState(doc) {
      var source = doc.querySelector('cdc-load-more');
      var total = toInt(this.dataset.totalItems, 0);

      if (source) {
        this.dataset.currentPage = source.dataset.currentPage || this.dataset.currentPage;
        this.dataset.shown = source.dataset.shown || this.dataset.shown;
        if (source.dataset.nextUrl) {
          this.dataset.nextUrl = source.dataset.nextUrl;
          if (this.button) this.button.setAttribute('href', source.dataset.nextUrl);
        } else {
          delete this.dataset.nextUrl;
        }
      } else {
        delete this.dataset.nextUrl;
      }

      var shown = toInt(this.dataset.shown, 0);
      if (this.bar && total > 0) this.bar.style.width = Math.round((shown / total) * 100) + '%';

      if (this.dataset.nextUrl) {
        this.setStatus(fill(this.i18n.status, shown, total));
      } else {
        this.setStatus(fill(this.i18n.complete, total));
        this.classList.add('is-complete');
        if (this.button) this.button.remove();
        this.button = null;
      }
    }

    setStatus(text) {
      if (this.status && text) this.status.textContent = text;
    }

    /* ------------------------------------------------ suivi d'URL au defilement

       Sert UNIQUEMENT a garder l'URL sur le lot visible : elle ne declenche
       jamais de chargement. Pas d'IntersectionObserver : un saut de defilement
       qui franchit plusieurs reperes d'un coup ne produit aucune entree, et
       l'URL resterait sur le mauvais lot. Une veille calee sur
       requestAnimationFrame lit la position reelle a chaque passe.
       ------------------------------------------------------------------- */

    setupScrollWatch() {
      var self = this;
      this.scrollTick = false;
      this.onScroll = function () {
        if (self.scrollTick) return;
        self.scrollTick = true;
        window.requestAnimationFrame(function () {
          self.scrollTick = false;
          self.trackUrl();
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    }

    /* Le lot « courant » est le dernier dont le repere est passe au-dessus du
       quart haut de l'ecran — vrai dans les deux sens de defilement. */
    trackUrl() {
      if (!this.anchors.length) return;
      var threshold = window.innerHeight * 0.25;
      var page = null;
      for (var i = 0; i < this.anchors.length; i++) {
        if (this.anchors[i].getBoundingClientRect().top <= threshold) {
          page = this.anchors[i].dataset.lmPage;
        }
      }
      /* Au-dessus du premier repere — en tete de page, ou dans le bandeau de
         filtres : on est encore sur le lot initial, pas sur le dernier charge. */
      if (!page) page = this.anchors[0].dataset.lmPage;
      this.syncUrl(page);
    }

    registerAnchor(anchor) {
      this.anchors.push(anchor);
    }

    /* replaceState et non pushState : l'URL reste partageable et rechargeable,
       mais le bouton Retour du navigateur sort de la collection au lieu de
       defaire les lots un par un. */
    syncUrl(page) {
      if (!page) return;
      var url = new URL(window.location.href);
      var current = url.searchParams.get('page') || '1';
      if (current === String(page)) return;
      if (String(page) === '1') url.searchParams.delete('page');
      else url.searchParams.set('page', page);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    /* ------------------------------------------- reprise de position au retour */

    setupAnchorCapture() {
      this.grid.addEventListener(
        'click',
        function (event) {
          var item = event.target.closest('[role="listitem"]');
          if (!item) return;
          var card = item.querySelector('[data-handle]');
          if (!card) return;
          try {
            sessionStorage.setItem(
              ANCHOR_KEY,
              JSON.stringify({ key: anchorKey(), handle: card.getAttribute('data-handle') })
            );
          } catch (e) {
            /* navigation privee : on se passe de la reprise */
          }
        },
        { passive: true }
      );
    }

    /* N'agit que sur un retour a froid (bfcache indisponible) : quand le
       bfcache fonctionne, connectedCallback n'est meme pas rejoue. */
    restoreAnchor() {
      var entries = performance.getEntriesByType && performance.getEntriesByType('navigation');
      if (!entries || !entries[0] || entries[0].type !== 'back_forward') return;

      var raw;
      try {
        raw = sessionStorage.getItem(ANCHOR_KEY);
        if (raw) sessionStorage.removeItem(ANCHOR_KEY);
      } catch (e) {
        return;
      }
      if (!raw) return;

      var saved;
      try {
        saved = JSON.parse(raw);
      } catch (e) {
        return;
      }
      if (!saved || !saved.handle || saved.key !== anchorKey()) return;

      var card = this.grid.querySelector('[data-handle="' + CSS.escape(saved.handle) + '"]');
      if (!card) return;
      (card.closest('[role="listitem"]') || card).scrollIntoView({ block: 'center' });
    }
  }

  customElements.define('cdc-load-more', CdcLoadMore);
})();
